<template>
    <div class="container-fluid page-card sales-dashboard">
        <header class="sales-dashboard__header">
            <div class="sales-dashboard__heading">
                <TitlePage
                    :title="$t('admin.salesAccess.title')"
                    :subtitle="$t('admin.salesAccess.subtitle')"
                    :hasMargin="false"
                />
            </div>
            <div class="sales-dashboard__actions" v-if="!isCorporateMode">
                <v-btn
                    variant="flat"
                    color="primary"
                    rounded="lg"
                    :title="$t('admin.salesAccess.new')"
                    :icon="$vuetify.display.xs"
                    class="text-none sales-dashboard__new"
                    @click="openDialog"
                >
                    <template v-if="$vuetify.display.xs">
                        <v-icon>mdi-plus</v-icon>
                    </template>
                    <template v-else>
                        <v-icon class="me-1" size="small">mdi-plus</v-icon>
                        {{ $t('admin.salesAccess.new') }}
                    </template>
                </v-btn>
            </div>
        </header>

        <div class="sales-dashboard__body">
        <section class="sales-dashboard__stats" :aria-label="$t('admin.reports.salesSummaryAria')">
            <article class="sales-dashboard__stat">
                <div class="sales-dashboard__stat-top">
                    <span class="sales-dashboard__stat-icon" aria-hidden="true">
                        <fa icon="fa-shopping-cart" />
                    </span>
                    <button
                        type="button"
                        class="sales-dashboard__stat-info"
                        :title="$t('admin.salesAccess.stats.totalSalesHelper')"
                        :aria-label="$t('admin.salesAccess.stats.totalSalesHelper')"
                    >
                        <fa icon="fa-circle-info" />
                    </button>
                </div>
                <div class="sales-dashboard__stat-content">
                    <p class="sales-dashboard__stat-label">{{ $t('admin.salesAccess.stats.totalSales') }}</p>
                    <p class="sales-dashboard__stat-value">{{ summaryCountLabel }}</p>
                </div>
            </article>
            <article class="sales-dashboard__stat">
                <div class="sales-dashboard__stat-top">
                    <span class="sales-dashboard__stat-icon" aria-hidden="true">
                        <fa icon="fa-coins" />
                    </span>
                    <button
                        type="button"
                        class="sales-dashboard__stat-info"
                        :title="$t('admin.salesAccess.stats.monthTotalHelper')"
                        :aria-label="$t('admin.salesAccess.stats.monthTotalHelper')"
                    >
                        <fa icon="fa-circle-info" />
                    </button>
                </div>
                <div class="sales-dashboard__stat-content">
                    <p class="sales-dashboard__stat-label">{{ $t('admin.salesAccess.stats.revenue') }}</p>
                    <p class="sales-dashboard__stat-value">{{ summaryMonthLabel }}</p>
                </div>
            </article>
            <article class="sales-dashboard__stat">
                <div class="sales-dashboard__stat-top">
                    <span class="sales-dashboard__stat-icon" aria-hidden="true">
                        <fa icon="fa-user" />
                    </span>
                    <button
                        type="button"
                        class="sales-dashboard__stat-info"
                        :title="$t('admin.salesAccess.stats.activeSubscriptionsHelper')"
                        :aria-label="$t('admin.salesAccess.stats.activeSubscriptionsHelper')"
                    >
                        <fa icon="fa-circle-info" />
                    </button>
                </div>
                <div class="sales-dashboard__stat-content">
                    <p class="sales-dashboard__stat-label">{{ $t('admin.salesAccess.stats.activeSubscriptions') }}</p>
                    <p class="sales-dashboard__stat-value">—</p>
                </div>
            </article>
            <article class="sales-dashboard__stat">
                <div class="sales-dashboard__stat-top">
                    <span class="sales-dashboard__stat-icon" aria-hidden="true">
                        <fa icon="fa-credit-card" />
                    </span>
                    <button
                        type="button"
                        class="sales-dashboard__stat-info"
                        :title="$t('admin.salesAccess.stats.averageTicketHelper')"
                        :aria-label="$t('admin.salesAccess.stats.averageTicketHelper')"
                    >
                        <fa icon="fa-circle-info" />
                    </button>
                </div>
                <div class="sales-dashboard__stat-content">
                    <p class="sales-dashboard__stat-label">{{ $t('admin.salesAccess.stats.averageTicket') }}</p>
                    <p class="sales-dashboard__stat-value">—</p>
                </div>
            </article>
        </section>

        <section class="sales-dashboard__table-card">
            <div class="sales-dashboard__filters">
                <label class="sales-dashboard__field sales-dashboard__field--search">
                    <span class="sales-dashboard__field-label">{{ $t('admin.salesAccess.filters.search') }}</span>
                    <span class="sales-dashboard__search">
                        <fa icon="fa-magnifying-glass" aria-hidden="true" />
                        <input
                            v-model="searchTerm"
                            class="sales-dashboard__input"
                            type="search"
                            :placeholder="$t('admin.salesAccess.searchPlaceholder')"
                            autocomplete="off"
                            @input="onSearch"
                            @search="onSearch"
                        />
                    </span>
                </label>

                <label class="sales-dashboard__field">
                    <span class="sales-dashboard__field-label">{{ $t('admin.salesAccess.filters.course') }}</span>
                    <input
                        v-model="courseTerm"
                        class="sales-dashboard__input"
                        type="search"
                        :placeholder="$t('admin.salesAccess.coursePlaceholder')"
                        autocomplete="off"
                        @input="onSearch"
                        @search="onSearch"
                    />
                </label>

                <div class="sales-dashboard__field sales-dashboard__field--period">
                    <span class="sales-dashboard__field-label">{{ $t('admin.salesAccess.filters.period') }}</span>
                    <div class="sales-dashboard__period">
                        <input
                            v-model="startDate"
                            class="sales-dashboard__input"
                            type="date"
                            :aria-label="$t('admin.salesAccess.filters.from')"
                            @change="onFilterChange"
                        />
                        <span aria-hidden="true">—</span>
                        <input
                            v-model="endDate"
                            class="sales-dashboard__input"
                            type="date"
                            :aria-label="$t('admin.salesAccess.filters.to')"
                            @change="onFilterChange"
                        />
                    </div>
                </div>

                <button
                    type="button"
                    class="sales-dashboard__export"
                    :disabled="exporting"
                    @click="exportOrders"
                >
                    <fa icon="fa-download" />
                    {{ exporting ? $t('admin.reports.exporting') : $t('admin.reports.export') }}
                </button>
            </div>

            <div class="sales-dashboard__table-toolbar">
                <p v-if="orders.length">
                    {{ $t('admin.salesAccess.showingCount', { count: orders.length, total: listTotal }) }}
                </p>
                <p v-else></p>
                <div class="sales-dashboard__toolbar-actions">
                    <button
                        v-if="hasFilters"
                        type="button"
                        class="sales-dashboard__clear"
                        @click="clearFilters"
                    >
                        {{ $t('admin.salesAccess.clearFilters') }}
                    </button>
                </div>
            </div>

            <Loader v-if="!hasReady" />

            <div v-else-if="loadFailed" class="sales-dashboard__empty" role="alert">
                <strong>{{ $t('admin.salesAccess.loadError') }}</strong>
            </div>

            <div v-else-if="!orders.length" class="sales-dashboard__empty">
                <strong>{{ hasFilters ? $t('admin.salesAccess.emptyFiltered') : $t('admin.salesAccess.emptyTitle') }}</strong>
                <p v-if="!hasFilters">{{ $t('admin.salesAccess.emptyLead') }}</p>
                <button
                    v-else
                    type="button"
                    class="sales-dashboard__clear"
                    @click="clearFilters"
                >
                    {{ $t('admin.salesAccess.clearFilters') }}
                </button>
            </div>

            <template v-else>
                <div class="sales-dashboard__table-wrapper">
                    <table class="sales-dashboard__table">
                        <thead>
                            <tr>
                                <th>{{ $t('admin.salesAccess.columns.student') }}</th>
                                <th>{{ $t('admin.salesAccess.columns.course') }}</th>
                                <th>{{ $t('admin.salesAccess.columns.type') }}</th>
                                <th>{{ $t('admin.salesAccess.columns.status') }}</th>
                                <th>{{ $t('admin.salesAccess.columns.payment') }}</th>
                                <th>{{ $t('admin.salesAccess.columns.date') }}</th>
                                <th class="is-end">{{ $t('admin.salesAccess.columns.total') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="order in orders" :key="order.id">
                                <td>
                                    <div class="sales-dashboard__student">
                                        <span class="sales-dashboard__avatar" aria-hidden="true">{{ initials(order.userName) }}</span>
                                        <span class="sales-dashboard__student-text">
                                            <strong>{{ order.userName || '—' }}</strong>
                                            <small v-if="studentEmail(order)">{{ studentEmail(order) }}</small>
                                        </span>
                                    </div>
                                </td>
                                <td>{{ order.courseName || $t('admin.salesAccess.noCourse') }}</td>
                                <td>
                                    <span
                                        v-if="orderTypeKey(order)"
                                        class="sales-dashboard__badge"
                                        :class="`sales-dashboard__badge--${orderTypeKey(order)}`"
                                    >
                                        {{ $t(`admin.salesAccess.types.${orderTypeKey(order)}`) }}
                                    </span>
                                    <span v-else>—</span>
                                </td>
                                <td>
                                    <span
                                        class="sales-dashboard__badge"
                                        :class="`sales-dashboard__badge--${statusTone(order.status)}`"
                                    >
                                        {{ statusLabel(order.status) }}
                                    </span>
                                </td>
                                <td>
                                    <span class="sales-dashboard__payment">
                                        <fa
                                            v-if="paymentIcon(order.typePayment)"
                                            :icon="paymentIcon(order.typePayment)"
                                            aria-hidden="true"
                                        />
                                        {{ paymentLabel(order.typePayment) }}
                                    </span>
                                </td>
                                <td class="is-nowrap">{{ formatDate(order.dateApproved || order.dateInsert) }}</td>
                                <td class="is-end">{{ formatCurrency(order.total) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <footer class="sales-dashboard__pagination">
                    <p>{{ $t('admin.salesAccess.showingRange', { from: rangeFrom, to: rangeTo, total: listTotal }) }}</p>
                    <v-pagination
                        v-if="totalPages > 1"
                        class="sales-dashboard__pages"
                        :model-value="currentPage + 1"
                        :length="totalPages"
                        :total-visible="5"
                        :disabled="!hasReady"
                        @update:model-value="onPageChange"
                    />
                    <span v-else></span>
                    <p>{{ $t('admin.salesAccess.perPage', { size: pageSize }) }}</p>
                </footer>
            </template>
        </section>
        </div>

        <v-dialog v-model="dialogOpen" max-width="560">
            <v-card class="modal-card">
                <v-card-title>
                    <h3>{{ $t('admin.salesAccess.dialogTitle') }}</h3>
                </v-card-title>
                <v-card-text>
                    <p class="sales-dashboard__lead">{{ $t('admin.salesAccess.dialogLead') }}</p>
                    <v-autocomplete
                        v-model="selectedStudent"
                        :items="students"
                        item-title="name"
                        item-value="id"
                        return-object
                        :label="$t('admin.salesAccess.student')"
                        variant="outlined"
                        density="compact"
                        :loading="loadingOptions"
                        :disabled="saving || loadingOptions"
                        :no-data-text="$t('admin.salesAccess.noStudents')"
                        class="weeduc-admin-v-field"
                        :menu-props="adminSelectMenuProps"
                    />
                    <v-autocomplete
                        v-model="selectedCourse"
                        :items="courses"
                        item-title="comercialName"
                        item-value="id"
                        return-object
                        :label="$t('admin.salesAccess.course')"
                        variant="outlined"
                        density="compact"
                        :loading="loadingOptions"
                        :disabled="saving || loadingOptions"
                        :no-data-text="$t('admin.salesAccess.noCourses')"
                        class="weeduc-admin-v-field"
                        :menu-props="adminSelectMenuProps"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        :disabled="saving"
                        @click="dialogOpen = false"
                    >
                        {{ $t('admin.salesAccess.cancel') }}
                    </v-btn>
                    <v-btn
                        class="flex-grow-1"
                        color="primary"
                        variant="flat"
                        :loading="saving"
                        :disabled="!selectedStudent || !selectedCourse || saving"
                        @click="grantAccess"
                    >
                        {{ $t('admin.salesAccess.confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { debounce } from 'lodash';
import { toast } from 'vue3-toastify';
import Loader from '@/components/Loader/loader.vue';
import TitlePage from '@/components/Weflix/titlePage.vue';
import reportsApi from '@/utils/reports';
import coursesApi, { unwrapSellableCatalog } from '@/utils/courses';
import usersApi from '@/utils/user';
import { intlLocale } from '@/i18n/locale';
import { isAdminCorporateMode } from '@/utils/corporate/adminDashboardMode';

export default {
    components: {
        Loader,
        TitlePage,
    },
    data() {
        return {
            orders: [],
            searchTerm: '',
            courseTerm: '',
            startDate: '',
            endDate: '',
            currentPage: 0,
            pageSize: 10,
            totalPages: 0,
            totalElements: 0,
            hasReady: false,
            loadFailed: false,
            loadRequestId: 0,
            exporting: false,
            summaryLoading: true,
            summaryFailed: false,
            totalSalesCount: null,
            monthlySalesTotal: null,
            dialogOpen: false,
            saving: false,
            loadingOptions: false,
            students: [],
            courses: [],
            selectedStudent: null,
            selectedCourse: null,
        };
    },
    computed: {
        ...mapState(['token', 'dark', 'company', 'websites', 'adminDashboardMode']),
        isCorporateMode() {
            void this.company;
            void this.websites;
            void this.adminDashboardMode;
            return isAdminCorporateMode();
        },
        adminSelectMenuProps() {
            return this.dark
                ? { contentClass: 'weeduc-admin-select-menu-dark' }
                : {};
        },
        hasFilters() {
            return Boolean(
                String(this.searchTerm || '').trim()
                || String(this.courseTerm || '').trim()
                || this.startDate
                || this.endDate,
            );
        },
        listTotal() {
            if (this.totalElements > 0) return this.totalElements;
            return this.orders.length;
        },
        rangeFrom() {
            if (!this.orders.length) return 0;
            return this.currentPage * this.pageSize + 1;
        },
        rangeTo() {
            if (!this.orders.length) return 0;
            return this.rangeFrom + this.orders.length - 1;
        },
        summaryCountLabel() {
            if (this.summaryLoading) return '…';
            if (this.summaryFailed || !Number.isFinite(this.totalSalesCount)) return '—';
            return this.formatCount(this.totalSalesCount);
        },
        summaryMonthLabel() {
            if (this.summaryLoading) return '…';
            if (this.summaryFailed || !Number.isFinite(this.monthlySalesTotal)) return '—';
            return this.formatCurrency(this.monthlySalesTotal);
        },
    },
    created() {
        this.loadOrders();
        this.loadSummary();
    },
    methods: {
        orderFilters() {
            const filters = {};
            const userName = String(this.searchTerm || '').trim();
            const courseName = String(this.courseTerm || '').trim();
            if (userName) filters.userName = userName;
            if (courseName) filters.courseName = courseName;
            Object.assign(filters, this.periodParams());
            return filters;
        },
        periodParams() {
            const start = this.startDate;
            const end = this.endDate;
            if (!start && !end) return {};
            // GET /report/order só aplica o período com as duas datas, e o fim
            // cai à meia-noite. O dia seguinte inclui o dia escolhido.
            return {
                startDate: start || '2000-01-01',
                endDate: this.nextIsoDate(end || this.todayIso()),
            };
        },
        todayIso() {
            return this.toIsoDate(new Date());
        },
        toIsoDate(date) {
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${date.getFullYear()}-${month}-${day}`;
        },
        nextIsoDate(isoDate) {
            const [year, month, day] = String(isoDate).split('-').map(Number);
            const date = new Date(year, month - 1, day);
            date.setDate(date.getDate() + 1);
            return this.toIsoDate(date);
        },
        async loadOrders() {
            const requestId = this.loadRequestId + 1;
            this.loadRequestId = requestId;
            this.hasReady = false;
            this.loadFailed = false;
            try {
                const response = await reportsApi.getDataOrder(
                    this.token,
                    this.orderFilters(),
                    { page: this.currentPage, size: this.pageSize },
                );
                if (requestId !== this.loadRequestId) return;
                const content = Array.isArray(response?.content) ? response.content : [];
                this.orders = content.slice().sort((a, b) => {
                    const left = new Date(b.dateApproved || b.dateInsert || 0).getTime();
                    const right = new Date(a.dateApproved || a.dateInsert || 0).getTime();
                    return left - right;
                });
                const reportedTotal = Number(response?.totalElements);
                this.totalElements = Number.isFinite(reportedTotal) && !(reportedTotal === 0 && content.length > 0)
                    ? reportedTotal
                    : content.length;
                this.totalPages = Number(response?.totalPages) || 0;
            } catch (error) {
                if (requestId !== this.loadRequestId) return;
                console.error(error);
                this.orders = [];
                this.totalPages = 0;
                this.totalElements = 0;
                this.loadFailed = true;
                toast(this.$t('admin.salesAccess.loadError'), { type: 'error' });
            } finally {
                if (requestId === this.loadRequestId) this.hasReady = true;
            }
        },
        async loadSummary() {
            this.summaryLoading = true;
            this.summaryFailed = false;
            try {
                const summary = await reportsApi.getDataOrderSummary(this.token);
                const count = Number(summary?.totalSalesCount);
                const month = Number(summary?.monthlySalesTotal);
                this.totalSalesCount = Number.isFinite(count) ? count : null;
                this.monthlySalesTotal = Number.isFinite(month) ? month : null;
            } catch (error) {
                console.error(error);
                this.summaryFailed = true;
                this.totalSalesCount = null;
                this.monthlySalesTotal = null;
                toast(this.$t('admin.reports.salesSummaryError'), { type: 'error' });
            } finally {
                this.summaryLoading = false;
            }
        },
        onSearch: debounce(function onSearch() {
            this.currentPage = 0;
            this.loadOrders();
        }, 300),
        onFilterChange() {
            this.currentPage = 0;
            this.loadOrders();
        },
        clearFilters() {
            this.onSearch.cancel();
            this.searchTerm = '';
            this.courseTerm = '';
            this.startDate = '';
            this.endDate = '';
            this.currentPage = 0;
            this.loadOrders();
        },
        onPageChange(page) {
            this.currentPage = Math.max(0, page - 1);
            this.loadOrders();
        },
        async exportOrders() {
            if (this.exporting) return;
            this.exporting = true;
            try {
                toast(this.$t('admin.reports.exporting'), { type: 'info' });
                const rows = await this.fetchAllOrders(this.orderFilters());
                const file = new Blob([this.ordersToCsv(rows)], { type: 'text/csv;charset=utf-8' });
                const url = window.URL.createObjectURL(file);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_vendas.csv');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast(this.$t('admin.reports.exportSuccess'), { type: 'success' });
            } catch (error) {
                toast(this.$t('admin.reports.exportError'), { type: 'error' });
                console.error(error);
            } finally {
                this.exporting = false;
            }
        },
        async fetchAllOrders(filters) {
            const size = 100;
            const rows = [];
            let page = 0;
            while (page < 200) {
                const response = await reportsApi.getDataOrder(
                    this.token,
                    filters,
                    { page, size },
                );
                const content = Array.isArray(response?.content) ? response.content : [];
                rows.push(...content);
                const totalPages = Number(response?.totalPages) || 0;
                page += 1;
                if (!content.length || content.length < size || (totalPages && page >= totalPages)) break;
            }
            return rows;
        },
        ordersToCsv(orders) {
            const header = [
                this.$t('admin.salesAccess.columns.student'),
                this.$t('admin.salesAccess.columns.course'),
                this.$t('admin.salesAccess.columns.type'),
                this.$t('admin.salesAccess.columns.status'),
                this.$t('admin.salesAccess.columns.payment'),
                this.$t('admin.salesAccess.columns.date'),
                this.$t('admin.salesAccess.columns.total'),
            ];
            const lines = [header.map((cell) => this.csvCell(cell)).join(';')];
            orders.forEach((order) => {
                const typeKey = this.orderTypeKey(order);
                lines.push([
                    order.userName || '',
                    order.courseName || this.$t('admin.salesAccess.noCourse'),
                    typeKey ? this.$t(`admin.salesAccess.types.${typeKey}`) : '',
                    this.statusLabel(order.status),
                    this.paymentLabel(order.typePayment),
                    this.formatDate(order.dateApproved || order.dateInsert),
                    this.formatCurrency(order.total),
                ].map((cell) => this.csvCell(cell)).join(';'));
            });
            return `\uFEFF${lines.join('\n')}`;
        },
        csvCell(value) {
            const text = String(value ?? '');
            if (/[";\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
            return text;
        },
        async openDialog() {
            this.dialogOpen = true;
            this.selectedStudent = null;
            this.selectedCourse = null;
            if (this.students.length && this.courses.length) return;
            this.loadingOptions = true;
            try {
                const [usersResponse, coursesResponse] = await Promise.all([
                    usersApi.getAllUser(this.token),
                    coursesApi.getDataCourse(this.token),
                ]);
                const users = Array.isArray(usersResponse?.content) ? usersResponse.content : [];
                this.students = users
                    .filter((user) => user && user.id && user.name && Number(user.status) === 1)
                    .map((user) => ({
                        id: user.id,
                        name: user.email ? `${user.name} · ${user.email}` : user.name,
                    }));
                this.courses = unwrapSellableCatalog(coursesResponse);
            } catch (error) {
                toast(this.$t('admin.salesAccess.optionsError'), { type: 'error' });
                console.error(error);
            } finally {
                this.loadingOptions = false;
            }
        },
        async grantAccess() {
            if (!this.selectedStudent || !this.selectedCourse) return;
            this.saving = true;
            try {
                await coursesApi.manualEnrol(this.token, {
                    tokenStudent: this.selectedStudent.id,
                    tokenCourse: this.selectedCourse.id,
                });
                toast(this.$t('admin.salesAccess.success', {
                    name: this.selectedStudent.name,
                    course: this.selectedCourse.comercialName,
                }), { type: 'success' });
                this.dialogOpen = false;
                this.currentPage = 0;
                await this.loadOrders();
            } catch (error) {
                toast(this.$t('admin.salesAccess.error'), { type: 'error' });
                console.error(error);
            } finally {
                this.saving = false;
            }
        },
        initials(name) {
            const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
            if (!parts.length) return '—';
            const first = parts[0].charAt(0);
            const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : parts[0].charAt(1);
            return `${first}${last || ''}`.toUpperCase();
        },
        studentEmail(order) {
            const email = order?.userEmail || order?.email || order?.user?.email || '';
            return String(email).trim();
        },
        orderTypeKey(order) {
            const payment = Number(order?.typePayment);
            const status = Number(order?.status);
            if (payment === 9) return 'manual';
            if (status === 1) return 'subscription';
            if (status === 4) return 'purchase';
            return '';
        },
        statusLabel(status) {
            const key = {
                0: 'waiting',
                1: 'active',
                2: 'expired',
                3: 'cancelled',
                4: 'paid',
                9: 'pix',
            }[Number(status)];
            if (!key) {
                return this.$t('admin.salesAccess.status.unknown', { status: status ?? '—' });
            }
            return this.$t(`admin.salesAccess.status.${key}`);
        },
        statusTone(status) {
            const value = Number(status);
            if (value === 1 || value === 4) return 'success';
            if (value === 2 || value === 3) return 'danger';
            return 'pending';
        },
        paymentLabel(typePayment) {
            const key = {
                1: 'card',
                2: 'pix',
                3: 'boleto',
                4: 'free',
                9: 'manual',
            }[Number(typePayment)];
            if (!key) {
                return this.$t('admin.salesAccess.payment.unknown', { type: typePayment ?? '—' });
            }
            return this.$t(`admin.salesAccess.payment.${key}`);
        },
        paymentIcon(typePayment) {
            return {
                1: 'fa-credit-card',
                2: 'fa-qrcode',
                3: 'fa-barcode',
                4: 'fa-gift',
                9: 'fa-user-check',
            }[Number(typePayment)] || '';
        },
        formatCount(value) {
            return Number(value).toLocaleString(intlLocale(this.$i18n.locale));
        },
        formatDate(value) {
            if (!value) return '—';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleString(intlLocale(this.$i18n.locale), {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        formatCurrency(value) {
            const amount = Number(value);
            return (Number.isFinite(amount) ? amount : 0).toLocaleString(
                intlLocale(this.$i18n.locale),
                { style: 'currency', currency: 'BRL' },
            );
        },
    },
};
</script>

<style scoped>
.sales-dashboard {
    --sales-primary: #3020a4;
    --sales-primary-hover: #4327c9;
    --sales-lilac: #f1eeff;
    --sales-bg: #f7f8fd;
    --sales-card: #ffffff;
    --sales-text: #11143d;
    --sales-muted: #64748b;
    --sales-border: #e7eaf2;
    --sales-label: #344054;
    --sales-input-border: #d9dfea;
    --sales-head: #f8f9fc;
    --sales-line: #edf0f5;
    --sales-hover: #faf9ff;
    box-sizing: border-box;
    height: auto;
    margin: 0;
    padding: 0 0 1rem;
    overflow: visible;
    background: #f4f5f8;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(37, 8, 145, 0.06);
    color: var(--sales-text);
    text-align: left;
}

.sales-dashboard *,
.sales-dashboard *::before,
.sales-dashboard *::after {
    box-sizing: border-box;
}

.sales-dashboard__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem 1.5rem;
    margin: 0;
    padding: 1.25rem 1.5rem 1rem;
    background: linear-gradient(135deg, #fafbff 0%, #ffffff 100%);
    border-bottom: 1px solid #e8eaf0;
    border-radius: 16px 16px 0 0;
}

.sales-dashboard__body {
    padding: 1.25rem 1.5rem 0.5rem;
}

.sales-dashboard__heading {
    flex: 1;
    min-width: 200px;
}

.sales-dashboard__heading :deep(.titlelayoutGeneric) {
    border-bottom: none;
    padding-bottom: 0;
}

.sales-dashboard__heading :deep(.titlelayoutGeneric h3) {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.35rem;
    color: #1a1d26;
}

.sales-dashboard__heading :deep(.titlelayoutGeneric small) {
    display: block;
    color: #5c6370;
    line-height: 1.45;
}

.sales-dashboard__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
}

.sales-dashboard__new {
    min-height: 40px;
    font-weight: 600;
}

.sales-dashboard__stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    width: 100%;
    margin-bottom: 16px;
}

.sales-dashboard__stat {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas: "icon content info";
    align-items: center;
    column-gap: 12px;
    min-width: 0;
    min-height: 0;
    padding: 14px 16px;
    background: var(--sales-card);
    border: 1px solid var(--sales-border);
    border-radius: 12px;
    box-shadow: 0 3px 14px rgba(20, 25, 60, 0.03);
    transition: border-color 150ms ease, box-shadow 150ms ease;
}

.sales-dashboard__stat:hover {
    border-color: #d6ccff;
    box-shadow: 0 6px 20px rgba(67, 39, 201, 0.06);
}

.sales-dashboard__stat-top {
    display: contents;
}

.sales-dashboard__stat-icon {
    display: flex;
    grid-area: icon;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 12px;
    background: var(--sales-lilac);
    color: #5035d9;
    font-size: 16px;
}

.sales-dashboard__stat-icon svg {
    color: #5035d9;
    fill: #5035d9;
}

.sales-dashboard__stat-content {
    display: flex;
    grid-area: content;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
}

.sales-dashboard__stat-label {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    color: var(--sales-muted);
}

.sales-dashboard__stat-info {
    display: inline-flex;
    grid-area: info;
    align-items: center;
    justify-content: center;
    align-self: start;
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #98a2b3;
    font-size: 15px;
    cursor: help;
}

.sales-dashboard__stat-info:hover,
.sales-dashboard__stat-info:focus-visible {
    background: var(--sales-lilac);
    color: #5035d9;
    outline: none;
}

.sales-dashboard__stat-info svg {
    color: currentColor;
    fill: currentColor;
}

.sales-dashboard__stat-value {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.4px;
    color: var(--sales-text);
    overflow-wrap: anywhere;
}

.sales-dashboard__table-card {
    background: var(--sales-card);
    border: 1px solid var(--sales-border);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 3px 14px rgba(20, 25, 60, 0.03);
}

.sales-dashboard__filters {
    display: grid;
    grid-template-columns: minmax(220px, 2fr) minmax(160px, 1fr) minmax(240px, 1.5fr) auto;
    gap: 12px;
    align-items: end;
    margin-bottom: 20px;
}

.sales-dashboard__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.sales-dashboard__field-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--sales-label);
}

.sales-dashboard__search {
    display: grid;
    align-items: center;
    min-width: 0;
}

.sales-dashboard__search svg {
    grid-area: 1 / 1;
    justify-self: start;
    width: 14px;
    height: 14px;
    margin-left: 12px;
    color: var(--sales-muted);
    fill: currentColor;
    pointer-events: none;
    z-index: 1;
}

.sales-dashboard__input,
.sales-dashboard__select {
    width: 100%;
    height: 42px;
    min-width: 0;
    border: 1px solid var(--sales-input-border);
    border-radius: 8px;
    padding: 0 12px;
    background: var(--sales-card);
    color: var(--sales-text);
    font: inherit;
    font-size: 13px;
}

.sales-dashboard__search .sales-dashboard__input {
    grid-area: 1 / 1;
    padding-left: 34px;
}

.sales-dashboard__input:focus,
.sales-dashboard__select:focus {
    outline: 2px solid var(--sales-primary);
    outline-offset: 1px;
}

.sales-dashboard__period {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    gap: 8px;
    align-items: center;
    color: var(--sales-muted);
}

.sales-dashboard__table-toolbar,
.sales-dashboard__pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.sales-dashboard__table-toolbar {
    margin-bottom: 12px;
    min-height: 32px;
}

.sales-dashboard__table-toolbar p,
.sales-dashboard__pagination p {
    margin: 0;
    font-size: 13px;
    color: var(--sales-muted);
}

.sales-dashboard__toolbar-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.sales-dashboard__clear,
.sales-dashboard__export {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    padding: 0 12px;
    border-radius: 8px;
    background: var(--sales-card);
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
}

.sales-dashboard__clear {
    border: 1px solid var(--sales-border);
    color: var(--sales-primary);
}

.sales-dashboard__export {
    align-self: end;
    height: 42px;
    padding: 0 16px;
    border: none;
    background: var(--sales-primary);
    color: #fff;
    -webkit-text-fill-color: #fff;
}

.sales-dashboard__export svg {
    color: #fff;
    fill: #fff;
}

.sales-dashboard__export:hover:not(:disabled) {
    background: var(--sales-primary-hover);
    color: #fff;
}

.sales-dashboard__export:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.sales-dashboard__table-wrapper {
    width: 100%;
    overflow-x: auto;
    border: 1px solid var(--sales-border);
    border-radius: 10px;
}

.sales-dashboard__table {
    width: 100%;
    min-width: 950px;
    border-collapse: collapse;
}

.sales-dashboard__table th {
    background: var(--sales-head);
    color: var(--sales-label);
    padding: 14px 12px;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    white-space: nowrap;
}

.sales-dashboard__table td {
    padding: 13px 12px;
    border-top: 1px solid var(--sales-line);
    font-size: 12px;
    vertical-align: middle;
    color: var(--sales-text);
}

.sales-dashboard__table tbody tr:hover {
    background: var(--sales-hover);
}

.sales-dashboard__table .is-end {
    text-align: right;
    white-space: nowrap;
}

.sales-dashboard__table .is-nowrap {
    white-space: nowrap;
}

.sales-dashboard__student {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 180px;
}

.sales-dashboard__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--sales-lilac);
    color: var(--sales-primary);
    font-size: 11px;
    font-weight: 700;
}

.sales-dashboard__student-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.sales-dashboard__student-text strong {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
}

.sales-dashboard__student-text small {
    color: var(--sales-muted);
    font-size: 11px;
    line-height: 1.3;
}

.sales-dashboard__badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
}

.sales-dashboard__badge--subscription {
    background: #f1eeff;
    color: #4327c9;
}

.sales-dashboard__badge--purchase {
    background: #e8f1ff;
    color: #1d4ed8;
}

.sales-dashboard__badge--manual {
    background: #f4f6fb;
    color: #475569;
}

.sales-dashboard__badge--success {
    background: #dcfce7;
    color: #15803d;
}

.sales-dashboard__badge--pending {
    background: #fff3e5;
    color: #c2410c;
}

.sales-dashboard__badge--danger {
    background: #fee2e2;
    color: #dc2626;
}

.sales-dashboard__payment {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
}

.sales-dashboard__pagination {
    padding-top: 20px;
}

.sales-dashboard__pages {
    flex: 0 1 auto;
}

.sales-dashboard__empty {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 28px 8px 12px;
}

.sales-dashboard__empty strong {
    font-size: 15px;
    color: var(--sales-text);
}

.sales-dashboard__empty p {
    margin: 0;
    font-size: 13px;
    color: var(--sales-muted);
}

.sales-dashboard__lead {
    margin-bottom: 16px;
    color: #475569;
}

@media (min-width: 768px) {
    .sales-dashboard.page-card {
        padding: 0 0 1rem;
    }
}

@media (max-width: 1200px) {
    .sales-dashboard__stats,
    .sales-dashboard__filters {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .sales-dashboard__field--search,
    .sales-dashboard__field--period {
        grid-column: 1 / -1;
    }
}

@media (max-width: 768px) {
    .sales-dashboard__filters {
        grid-template-columns: 1fr;
    }

    .sales-dashboard__field--search,
    .sales-dashboard__field--period {
        grid-column: auto;
    }

    .sales-dashboard__export {
        width: 100%;
    }

    .sales-dashboard__table-card {
        padding: 14px;
    }

    .sales-dashboard__pagination {
        flex-wrap: wrap;
        justify-content: center;
    }

    .sales-dashboard__period {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 640px) {
    .sales-dashboard__stats {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .sales-dashboard__stat {
        padding: 12px 14px;
    }

    .sales-dashboard__stat-value {
        font-size: 20px;
    }
}
</style>

<style>
.dashboard.dark .sales-dashboard {
    --sales-bg: #13121a;
    --sales-card: #201e26;
    --sales-text: #f4f5fb;
    --sales-muted: #a6a6b8;
    --sales-border: #2d2d38;
    --sales-label: #d5d7e2;
    --sales-input-border: #3a3a48;
    --sales-head: #292733;
    --sales-line: #2d2d38;
    --sales-hover: #2a2738;
    --sales-lilac: #2c2750;
}

.dashboard.dark .sales-dashboard.page-card {
    background: #1c1d24;
    border: 1px solid #2f323c;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.dashboard.dark .sales-dashboard__header {
    background: linear-gradient(145deg, #23252e 0%, #1c1d24 100%);
    border-bottom-color: #353945;
}

.dashboard.dark .sales-dashboard__heading .titlelayoutGeneric h3 {
    color: #f4f5fb;
}

.dashboard.dark .sales-dashboard__heading .titlelayoutGeneric small {
    color: #a6a6b8;
}

.dashboard.dark .sales-dashboard__badge--manual {
    background: #2a2d38;
    color: #d5d7e2;
}
</style>
